import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Dumbbell, Timer, Flame, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function StrengthTrainingGuide() {
  const navigate = useNavigate();

  const exercises = [
    {
      name: "Squats",
      reps: "10-15 reps",
      muscles: "Quads, glutes, core",
      instructions: ["Stand with feet shoulder-width apart", "Push hips back and bend knees", "Lower until thighs are parallel to floor", "Press through heels to stand", "Keep chest lifted throughout"],
      modification: "Use a chair behind you for support"
    },
    {
      name: "Push-Ups",
      reps: "8-12 reps",
      muscles: "Chest, shoulders, triceps, core",
      instructions: ["Start in plank position, hands wider than shoulders", "Lower chest toward floor, elbows at 45 degrees", "Push back up to start", "Keep body in straight line", "Engage core throughout"],
      modification: "Do on knees or against a wall"
    },
    {
      name: "Lunges",
      reps: "10 each leg",
      muscles: "Quads, glutes, hamstrings",
      instructions: ["Stand tall, step one foot forward", "Lower back knee toward floor", "Keep front knee over ankle", "Push through front heel to stand", "Alternate legs"],
      modification: "Hold onto a chair for balance"
    },
    {
      name: "Plank",
      reps: "30-60 seconds",
      muscles: "Core, shoulders, back",
      instructions: ["Forearms on floor, elbows under shoulders", "Body in straight line from head to heels", "Engage core, don't let hips sag or pike", "Breathe steadily", "Keep neck neutral"],
      modification: "Drop to knees to make it easier"
    },
    {
      name: "Tricep Dips",
      reps: "10-12 reps",
      muscles: "Triceps, shoulders",
      instructions: ["Sit on edge of sturdy chair, hands beside hips", "Slide hips off chair, legs extended or bent", "Lower body by bending elbows to 90 degrees", "Press back up", "Keep back close to chair"],
      modification: "Bend knees more to reduce difficulty"
    },
    {
      name: "Glute Bridges",
      reps: "15 reps",
      muscles: "Glutes, hamstrings, lower back",
      instructions: ["Lie on back, knees bent, feet flat", "Press through heels to lift hips", "Squeeze glutes at the top", "Lower with control", "Keep core engaged"],
      modification: "Hold at top for 3 seconds for extra challenge"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
            Week 4 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Strength Training at Home
          </h1>
          <p className="text-xl text-secondary-600">No Equipment Needed • 30 Minutes • All Fitness Levels</p>
        </div>

        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/8d23ae89e2c4d638a7375a066bed85bf02e283682831a42de3850c26f03168e3.png"
            alt="Strength Training"
            className="w-full h-auto object-cover"
          />
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Strength Training Matters After 40</h2>
          <p className="text-secondary-700 mb-4">
            After 40, we lose about 1% of muscle mass per year if we don't actively work to maintain it. This leads to decreased metabolism, increased injury risk, and reduced functional strength. The good news? Strength training can reverse this—at any age.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {[
              { icon: Flame, title: "Boosts Metabolism", desc: "Muscle burns more calories at rest" },
              { icon: Dumbbell, title: "Strengthens Bones", desc: "Reduces osteoporosis risk" },
              { icon: Timer, title: "Just 30 Min", desc: "2-3x per week is enough" }
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-orange-50 rounded-xl">
                <item.icon className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                <h3 className="font-bold text-secondary-900 text-sm">{item.title}</h3>
                <p className="text-xs text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-6 text-white mb-6">
          <h3 className="font-bold text-lg mb-2">The Workout: Do 3 Rounds</h3>
          <p className="text-orange-100">Complete all exercises, rest 60 seconds between rounds. Total time: ~30 minutes</p>
        </section>

        <section className="space-y-4 mb-6">
          {exercises.map((ex, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-700 font-bold">{i+1}</span>
                  <div>
                    <h3 className="text-xl font-bold text-secondary-900">{ex.name}</h3>
                    <p className="text-sm text-secondary-500">{ex.muscles}</p>
                  </div>
                </div>
                <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-bold">{ex.reps}</span>
              </div>
              <ul className="space-y-1 mb-3">
                {ex.instructions.map((inst, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-secondary-700">
                    <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />{inst}
                  </li>
                ))}
              </ul>
              <div className="bg-orange-50 rounded-lg p-3">
                <p className="text-sm text-orange-800"><AlertCircle className="w-4 h-4 inline mr-1" /><strong>Easier option:</strong> {ex.modification}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Important Tips</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Warm up first", tip: "March in place or do jumping jacks for 3-5 minutes" },
              { title: "Focus on form", tip: "Quality over quantity—proper form prevents injury" },
              { title: "Breathe properly", tip: "Exhale on the effort (pushing/lifting), inhale on the release" },
              { title: "Rest between sessions", tip: "Give muscles 48 hours to recover before training same muscles" },
              { title: "Progress gradually", tip: "Add reps or sets as exercises become easier" },
              { title: "Listen to your body", tip: "Discomfort is normal; sharp pain is not—stop if it hurts" }
            ].map((item, i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-xl">
                <h3 className="font-bold text-secondary-900 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.tip}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default StrengthTrainingGuide;
